<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="960" scrollable
        content-class="ms-dialog-wrap">
        <div class="ms-dialog">
            <!-- HEADER -->
            <header class="ms-dlg-header">
                <div class="ms-dlg-header__main">
                    <span class="ms-dlg-header__num">#{{ item.request_number || '—' }}</span>
                    <div class="ms-dlg-header__text">
                        <h2 class="ms-dlg-header__title">{{ item.subject || typeLabel(item.type) }}</h2>
                        <div class="ms-dlg-header__meta">
                            <span class="ms-status-pill" :class="`ms-status-pill--${statusModifier(item.status)}`">
                                <span class="ms-status-pill__dot"></span>
                                {{ statusLabel(item.status) }}
                            </span>
                            <span class="ms-priority" :class="`ms-priority--${priorityModifier(item.priority)}`">
                                <v-icon size="14">{{ priorityIcon(item.priority) }}</v-icon>
                                {{ priorityLabel(item.priority) }}
                            </span>
                            <span class="ms-dlg-header__date">
                                <v-icon size="14">mdi-calendar-blank-outline</v-icon>
                                נפתחה {{ formatDateString(item.createdAt) }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="ms-dlg-header__actions">
                    <button type="button" class="ms-dlg-edit-btn" @click="$emit('editRequest', item)">
                        <v-icon size="16">mdi-pencil-outline</v-icon>
                        עריכה
                    </button>
                    <button type="button" class="ms-dlg-close" @click="close" aria-label="סגירה">
                        <v-icon size="20">mdi-close</v-icon>
                    </button>
                </div>
            </header>

            <!-- BODY -->
            <div class="ms-dlg-body">
                <div class="ms-dlg-grid">
                    <!-- פרטי קריאה -->
                    <section class="ms-card">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-clipboard-text-outline</v-icon>
                            פרטי קריאה
                        </h3>
                        <dl class="ms-fields">
                            <div class="ms-field">
                                <dt>מספר קריאה</dt>
                                <dd class="ms-mono ms-dd-copy">
                                    <span class="ms-selectable">{{ item.request_number || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'request_number' }"
                                        @click="copy(item.request_number, 'request_number')" :aria-label="copiedKey === 'request_number' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'request_number' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>סוג</dt>
                                <dd>
                                    <span class="ms-type-badge">
                                        <v-icon size="14">{{ typeIcon(item.type) }}</v-icon>
                                        {{ typeLabel(item.type) }}
                                    </span>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>נושא</dt>
                                <dd class="ms-dd-copy">
                                    <span class="ms-selectable">{{ item.subject || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'subject' }"
                                        @click="copy(item.subject, 'subject')" :aria-label="copiedKey === 'subject' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'subject' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>מטפל בקריאה</dt>
                                <dd>{{ (item.assigned_to && item.assigned_to.name) || 'לא הוקצה' }}</dd>
                            </div>
                            <div class="ms-field">
                                <dt>משווק</dt>
                                <dd>{{ (item.seller_id && item.seller_id.name) || '—' }}</dd>
                            </div>
                            <div class="ms-field">
                                <dt>ספק / מפעיל</dt>
                                <dd>
                                    <span v-if="item.supplier_name" class="ms-supplier">
                                        <v-icon size="13">mdi-domain</v-icon>
                                        {{ supplierLabel(item.supplier_name) }}
                                    </span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>נשלח לספק</dt>
                                <dd>{{ item.sent_to_supplier_at ? formatDateString(item.sent_to_supplier_at) : 'טרם נשלח' }}</dd>
                            </div>
                            <div class="ms-field" v-if="item.completed_at">
                                <dt>הושלמה בתאריך</dt>
                                <dd>{{ formatDateString(item.completed_at) }}</dd>
                            </div>
                            <div class="ms-field">
                                <dt>עודכנה לאחרונה</dt>
                                <dd>{{ formatDateString(item.updatedAt) }}</dd>
                            </div>
                        </dl>
                    </section>

                    <!-- לקוח ומנוי -->
                    <section class="ms-card">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-account-outline</v-icon>
                            לקוח ומנוי
                        </h3>
                        <dl class="ms-fields">
                            <div class="ms-field">
                                <dt>לקוח</dt>
                                <dd>
                                    <button v-if="item.customer_id && item.customer_id._id" type="button"
                                        class="ms-link" @click="goToCustomer">
                                        {{ customerName }}
                                        <v-icon size="13">mdi-open-in-new</v-icon>
                                    </button>
                                    <span v-else>{{ customerName }}</span>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>ת״ז / ח״פ</dt>
                                <dd class="ms-mono ms-dd-copy">
                                    <span class="ms-selectable">{{ (item.customer_id && item.customer_id.card_id) || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'card_id' }"
                                        @click="copy(item.customer_id && item.customer_id.card_id, 'card_id')" :aria-label="copiedKey === 'card_id' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'card_id' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>טלפון</dt>
                                <dd class="ms-mono ms-dd-copy">
                                    <span class="ms-selectable">{{ (item.customer_id && item.customer_id.phone1) || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'phone1' }"
                                        @click="copy(item.customer_id && item.customer_id.phone1, 'phone1')" :aria-label="copiedKey === 'phone1' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'phone1' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>מספר קו</dt>
                                <dd class="ms-mono ms-mono--strong ms-dd-copy">
                                    <span class="ms-selectable">{{ item.line_number || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'line_number' }"
                                        @click="copy(item.line_number, 'line_number')" :aria-label="copiedKey === 'line_number' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'line_number' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>מספר סים</dt>
                                <dd class="ms-mono ms-dd-copy">
                                    <span class="ms-selectable">{{ item.sim_number || '—' }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'sim_number' }"
                                        @click="copy(item.sim_number, 'sim_number')" :aria-label="copiedKey === 'sim_number' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'sim_number' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field" v-if="item.manui_id && item.manui_id.manui_number">
                                <dt>מנוי משויך</dt>
                                <dd class="ms-mono ms-dd-copy">
                                    <span class="ms-selectable">{{ item.manui_id.manui_number }}</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'manui_number' }"
                                        @click="copy(item.manui_id.manui_number, 'manui_number')" :aria-label="copiedKey === 'manui_number' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'manui_number' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <!-- ניוד -->
                    <section class="ms-card" v-if="showPortingCard">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-swap-horizontal</v-icon>
                            פרטי ניוד
                        </h3>
                        <dl class="ms-fields">
                            <div class="ms-field">
                                <dt>מספר זמני</dt>
                                <dd class="ms-dd-copy">
                                    <span v-if="item.temp_number" class="ms-temp-badge ms-selectable">
                                        <v-icon size="12">mdi-clock-fast</v-icon>
                                        {{ item.temp_number }}
                                    </span>
                                    <span v-else>—</span>
                                    <button type="button" class="ms-copy" :class="{ 'ms-copy--done': copiedKey === 'temp_number' }"
                                        @click="copy(item.temp_number, 'temp_number')" :aria-label="copiedKey === 'temp_number' ? 'הועתק' : 'העתקה'">
                                        <v-icon size="13">{{ copiedKey === 'temp_number' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </button>
                                </dd>
                            </div>
                            <div class="ms-field">
                                <dt>חברה נטושה</dt>
                                <dd>{{ item.abandoned_company || '—' }}</dd>
                            </div>

                        </dl>
                    </section>

                    <!-- תיאור והערות -->
                    <section class="ms-card ms-card--wide">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-text-long</v-icon>
                            תיאור והערות
                            <button v-if="item.description" type="button" class="ms-copy ms-copy--inline"
                                :class="{ 'ms-copy--done': copiedKey === 'description' }"
                                @click="copy(item.description, 'description')">
                                <v-icon size="14">{{ copiedKey === 'description' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                {{ copiedKey === 'description' ? 'הועתק' : 'העתקת התיאור' }}
                            </button>
                        </h3>
                        <div class="ms-textblock ms-selectable" v-if="item.description">{{ item.description }}</div>
                        <p v-else class="ms-textblock ms-textblock--empty">לא הוזן תיאור לקריאה זו.</p>

                        <div v-if="item.internal_notes" class="ms-note">
                            <span class="ms-note__label">
                                <v-icon size="14">mdi-lock-outline</v-icon>
                                הערה פנימית
                                <button type="button" class="ms-copy ms-copy--inline"
                                    :class="{ 'ms-copy--done': copiedKey === 'internal_notes' }"
                                    @click="copy(item.internal_notes, 'internal_notes')">
                                    <v-icon size="13">{{ copiedKey === 'internal_notes' ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    {{ copiedKey === 'internal_notes' ? 'הועתק' : 'העתקה' }}
                                </button>
                            </span>
                            <div class="ms-note__text ms-selectable">{{ item.internal_notes }}</div>
                        </div>
                    </section>

                    <!-- קבצים -->
                    <section class="ms-card ms-card--wide">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-paperclip</v-icon>
                            קבצים
                            <span class="ms-card__count" v-if="allFiles.length">{{ allFiles.length }}</span>
                        </h3>

                        <div v-if="allFiles.length" class="ms-files-grid">
                            <div v-for="(f, i) in allFiles" :key="i" class="ms-file">
                                <button type="button" class="ms-file__main" @click="openFile(f.file)"
                                    :aria-label="`פתיחת ${f.label}`">
                                    <span class="ms-file__thumb" :class="{ 'ms-file__thumb--pdf': !isImage(f.file) }">
                                        <img v-if="isImage(f.file)" :src="fileUrl(f.file)" :alt="f.label"
                                            class="ms-file__img" loading="lazy" @error="onThumbError" />
                                        <v-icon v-else size="26">mdi-file-pdf-box</v-icon>
                                    </span>
                                    <span class="ms-file__info">
                                        <span class="ms-file__label">{{ f.label }}</span>
                                        <span class="ms-file__meta">
                                            {{ isImage(f.file) ? 'תמונה' : 'PDF' }}
                                            <template v-if="f.file.size"> · {{ fileSize(f.file.size) }}</template>
                                        </span>
                                    </span>
                                </button>

                                <div class="ms-file__actions">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <button v-on="on" type="button" class="ms-file__btn"
                                                @click="openFile(f.file)" aria-label="פתיחה בלשונית חדשה">
                                                <v-icon size="16">mdi-open-in-new</v-icon>
                                            </button>
                                        </template>
                                        <span>פתיחה בלשונית חדשה</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <button v-on="on" type="button" class="ms-file__btn ms-file__btn--dl"
                                                :disabled="downloadingIndex === i" @click="downloadFile(f.file, i)"
                                                aria-label="הורדת הקובץ">
                                                <v-progress-circular v-if="downloadingIndex === i" indeterminate
                                                    size="14" width="2" color="#3F51B5" />
                                                <v-icon v-else size="16">mdi-download-outline</v-icon>
                                            </button>
                                        </template>
                                        <span>הורדה למחשב</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>

                        <p v-else class="ms-textblock ms-textblock--empty">
                            לא צורפו קבצים לקריאה זו. צירוף קבצים מתבצע במערכת המקור.
                        </p>
                    </section>

                    <!-- היסטוריית סטטוסים -->
                    <section class="ms-card ms-card--wide" v-if="statusHistory.length">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-history</v-icon>
                            מסלול הקריאה
                        </h3>
                        <ol class="ms-timeline">
                            <li v-for="(h, i) in statusHistory" :key="i" class="ms-timeline__item"
                                :class="{ 'ms-timeline__item--last': i === statusHistory.length - 1 }">
                                <span class="ms-timeline__dot"
                                    :class="`ms-timeline__dot--${statusModifier(h.status)}`"></span>
                                <div class="ms-timeline__body">
                                    <span class="ms-timeline__status">{{ statusLabel(h.status) }}</span>
                                    <span class="ms-timeline__date">{{ formatDateString(h.changed_at) }}</span>
                                    <span class="ms-timeline__by" v-if="h.changed_by && h.changed_by.name">
                                        על ידי {{ h.changed_by.name }}
                                    </span>
                                    <span class="ms-timeline__note" v-if="h.note">{{ h.note }}</span>
                                </div>
                            </li>
                        </ol>
                    </section>
                </div>
            </div>

            <!-- FOOTER -->
            <footer class="ms-dlg-footer">
                <button type="button" class="ms-btn ms-btn--ghost" @click="close">סגירה</button>
                <button type="button" class="ms-btn ms-btn--primary"
                    @click="$emit('editRequest', item)">
                    <v-icon size="16">mdi-pencil-outline</v-icon>
                    עריכת פרטים
                </button>
            </footer>
        </div>
    </v-dialog>
</template>

<script>
import Utils from '@/util/utils'
import SRConsts from '@/util/serviceRequestsConsts'

export default {
    props: {
        value: { type: Boolean, default: false },
        item: { type: Object, default: () => ({}) },
    },
    data: () => ({
        copiedKey: '',
        downloadingIndex: -1,
    }),
    computed: {
        customerName() {
            const c = this.item.customer_id;
            if (!c) return '—';
            return `${c.name || ''} ${c.family || ''}`.trim() || '—';
        },
        showPortingCard() {
            return this.item.type === 'porting' || this.item.is_porting || !!this.item.temp_number;
        },
        allFiles() {
            const files = [];
            if (this.item.id_card_file && this.item.id_card_file.path) {
                files.push({ label: 'תמונת ת״ז', file: this.item.id_card_file });
            }
            if (this.item.supplier_form_file && this.item.supplier_form_file.path) {
                files.push({ label: 'טופס לספק', file: this.item.supplier_form_file });
            }
            (this.item.attachments || []).forEach((f, i) => {
                if (f && f.path) files.push({ label: `קובץ נוסף ${i + 1}`, file: f });
            });
            return files;
        },
        statusHistory() {
            // מהחדש לישן, כמו בשאר המערכת
            return [...(this.item.status_history || [])].reverse();
        },
    },
    methods: {
        statusLabel: SRConsts.statusLabel,
        statusModifier: SRConsts.statusModifier,
        priorityLabel: SRConsts.priorityLabel,
        priorityIcon: SRConsts.priorityIcon,
        priorityModifier: SRConsts.priorityModifier,
        typeLabel: SRConsts.typeLabel,
        typeIcon: SRConsts.typeIcon,
        supplierLabel: SRConsts.supplierLabel,

        formatDateString(date) {
            return date ? Utils.formatDateString(date) : '—';
        },
        /**
         * מתקן שם קובץ שנשמר כ-mojibake: UTF-8 שפורש כ-latin1
         * (busboy מפרש שמות ב-multipart כ-latin1 כברירת מחדל).
         * "×ª×¢×•×“×ª" חוזר להיות "תעודת".
         */
        repairName(name) {
            if (!name) return '';
            // אין תווי latin1 גבוהים – השם כבר תקין
            if (!/[\u00C0-\u00FF]/.test(name)) return name;
            try {
                const bytes = Uint8Array.from([...name].map(c => c.charCodeAt(0) & 0xFF));
                // fatal: true – אם זו לא רצף UTF-8 חוקי נשארים עם המקור
                return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
            } catch (error) {
                return name;
            }
        },

        // שם הקובץ מתוך הנתיב. תמיד תקין, כי הוא נבנה בשרת מ-card_id וחותמת זמן.
        nameFromPath(file) {
            return String(file.path || '').split('/').pop().split('?')[0] || '';
        },

        displayName(file) {
            const repaired = this.repairName((file.original_name || '').trim());
            return repaired || this.nameFromPath(file) || 'קובץ';
        },

        /**
         * שם הקובץ להורדה. חייב סיומת תקינה – בלעדיה Windows לא יודע
         * באיזו תוכנה לפתוח, וזו הסיבה שנמענים לא הצליחו לפתוח את הקובץ.
         */
        downloadName(file) {
            const fromPath = this.nameFromPath(file);
            let name = this.repairName((file.original_name || '').trim()) || fromPath || 'file';

            // מוסיפים סיומת מהנתיב אם חסרה בשם
            const ext = (fromPath.match(/\.[a-z0-9]{2,5}$/i) || [''])[0];
            if (ext && !name.toLowerCase().endsWith(ext.toLowerCase())) {
                name += ext;
            }

            // תווים שמערכות קבצים לא מקבלות
            return name.replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_');
        },

        isImage(file) {
            return !!(file && file.mime_type && file.mime_type.startsWith('image/'));
        },
        fileSize(bytes) {
            if (!bytes) return '';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
            return `${(bytes / 1048576).toFixed(1)} MB`;
        },
        fileUrl: SRConsts.fileUrl,

        openFile(file) {
            const url = SRConsts.fileUrl(file);
            if (!url) return;
            window.open(url, '_blank', 'noopener');
        },
        onThumbError(event) {
            // תמונה שלא נטענה (טוקן שפג / קובץ חסר) – מוחלפים באייקון במקום ריבוע שבור
            event.target.style.display = 'none';
        },
        async copy(text, key) {
            const value = text === null || text === undefined ? '' : String(text);
            if (!value || value === '—') return;

            try {
                await navigator.clipboard.writeText(value);
            } catch (error) {
                // navigator.clipboard לא זמין ב-http לא מאובטח – נפילה לשיטה הישנה
                const ta = document.createElement('textarea');
                ta.value = value;
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                ta.remove();
            }

            this.copiedKey = key;
            clearTimeout(this._copyTimer);
            this._copyTimer = setTimeout(() => { this.copiedKey = ''; }, 1800);
        },
        async downloadFile(file, index) {
            if (!file || !file.path) return;

            this.downloadingIndex = index;
            try {
                // מורידים דרך blob: התכונה download לא עובדת על קובץ מדומיין אחר
                const token = localStorage.getItem("token");
                const res = await fetch(SRConsts.fileUrl(file), {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error(res.status);
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.downloadName(file);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                // חסימת CORS – נפילה לפתיחה בלשונית, המשתמש יוריד משם
                window.open(SRConsts.fileUrl(file), '_blank', 'noopener');
            } finally {
                this.downloadingIndex = -1;
            }
        },
        goToCustomer() {
            const c = this.item.customer_id;
            if (!c || !c._id) return;
            const routeData = this.$router.resolve({
                name: "manuim",
                params: {
                    id: c._id,
                    name: `${c.name || ''} ${c.family || ''}`.trim() || '—',
                    cardId: c.card_id || '—'
                }
            });
            window.open(routeData.href, '_blank');
        },
        close() {
            this.$emit('input', false);
        },
    },
    beforeDestroy() {
        clearTimeout(this._copyTimer);
    },
}
</script>

<style scoped>
.ms-dialog {
    --m-navy: #0d2c6d;
    --m-indigo: #3F51B5;
    --m-indigo-soft: rgba(63, 81, 181, 0.10);
    --m-purple: #8066f0;
    --m-purple-soft: rgba(128, 102, 240, 0.10);
    --m-gold: #fdb721;
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-slate: #64748b;
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    display: flex;
    flex-direction: column;
    max-height: 92vh;
    background: #f7f9fc;
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, 'Segoe UI', sans-serif;
}

/* ── HEADER ── */
.ms-dlg-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 16px 20px;
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
}

.ms-dlg-header__main {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
}

.ms-dlg-header__num {
    flex-shrink: 0;
    padding: 5px 11px;
    border-radius: 9px;
    background: rgba(253, 183, 33, 0.20);
    border: 1px solid rgba(253, 183, 33, 0.38);
    color: #ffd77a;
    font-size: 0.95rem;
    font-weight: 800;
    font-feature-settings: "tnum" 1;
}

.ms-dlg-header__text {
    min-width: 0;
}

.ms-dlg-header__title {
    margin: 0 0 7px;
    font-size: 1.1rem;
    font-weight: 800;
    line-height: 1.3;
}

.ms-dlg-header__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
}

.ms-dlg-header__date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.78rem;
    font-weight: 600;
}

.ms-dlg-header__date ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.6) !important;
}

.ms-dlg-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.ms-dlg-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
    font-family: inherit;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-dlg-edit-btn:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ms-dlg-edit-btn ::v-deep .v-icon {
    color: #fff !important;
}

.ms-dlg-close {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.10);
    border: none;
    cursor: pointer;
    transition: background 0.15s var(--m-ease);
}

.ms-dlg-close:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ms-dlg-close ::v-deep .v-icon {
    color: #fff !important;
}

/* ── BODY ── */
.ms-dlg-body {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
}

.ms-dlg-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.ms-card {
    padding: 14px 16px 16px;
    border-radius: 13px;
    background: #fff;
    border: 1px solid var(--m-border-soft);
    box-shadow: 0 1px 3px rgba(13, 44, 109, 0.05);
}

.ms-card--wide {
    grid-column: 1 / -1;
}

.ms-card__title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 12px;
    padding-bottom: 9px;
    border-bottom: 1px solid var(--m-border-soft);
    color: var(--m-navy);
    font-size: 0.9rem;
    font-weight: 800;
}

.ms-card__title ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

.ms-card__count {
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-size: 0.72rem;
    font-weight: 800;
}

/* ── FIELDS ── */
.ms-fields {
    display: flex;
    flex-direction: column;
    gap: 1px;
    margin: 0;
}

.ms-field {
    display: grid;
    grid-template-columns: 130px 1fr;
    gap: 10px;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px dashed var(--m-border-soft);
}

.ms-field:last-child {
    border-bottom: none;
}

.ms-field dt {
    color: var(--m-text-mute);
    font-size: 0.79rem;
    font-weight: 600;
}

.ms-field dd {
    margin: 0;
    color: var(--m-text);
    font-size: 0.87rem;
    font-weight: 600;
    word-break: break-word;
}

.ms-mono {
    direction: ltr;
    text-align: right;
    font-feature-settings: "tnum" 1;
}

.ms-mono--strong {
    font-weight: 800;
    color: var(--m-navy);
}

/* ── COPY ── */
.ms-dd-copy {
    display: flex;
    align-items: center;
    gap: 6px;
}

.ms-selectable {
    user-select: text;
}

.ms-selectable::selection {
    background: rgba(63, 81, 181, 0.25);
}

.ms-copy {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 6px;
    background: transparent;
    border: none;
    color: var(--m-text-mute);
    cursor: pointer;
    opacity: 0;
    transition: all 0.15s var(--m-ease);
}

.ms-field:hover .ms-copy,
.ms-copy:focus-visible {
    opacity: 1;
}

.ms-copy:hover {
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
}

.ms-copy ::v-deep .v-icon {
    color: currentColor !important;
}

.ms-copy--done {
    opacity: 1;
    background: var(--m-green-soft);
    color: var(--m-green);
}

.ms-copy--inline {
    width: auto;
    height: auto;
    gap: 4px;
    display: inline-flex;
    align-items: center;
    margin-inline-start: auto;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-family: inherit;
    font-size: 0.72rem;
    font-weight: 700;
    opacity: 1;
}

.ms-copy--inline:hover {
    background: var(--m-indigo);
    color: #fff;
}

/* על מסכי מגע אין hover – הכפתורים תמיד גלויים */
@media (hover: none) {
    .ms-copy {
        opacity: 1;
    }
}

.ms-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    background: none;
    border: none;
    color: var(--m-indigo);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 700;
    cursor: pointer;
}

.ms-link:hover {
    text-decoration: underline;
}

.ms-link ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

/* ── BADGES (shared) ── */
.ms-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-status-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.ms-status-pill--open {
    background: var(--m-blue-soft);
    color: var(--m-blue);
}

.ms-status-pill--progress {
    background: var(--m-orange-soft);
    color: var(--m-orange);
}

.ms-status-pill--completed {
    background: var(--m-green-soft);
    color: var(--m-green);
}

.ms-status-pill--canceled {
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-priority {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    font-size: 0.77rem;
    font-weight: 700;
    color: #fff;
}

.ms-priority ::v-deep .v-icon {
    color: currentColor !important;
}

.ms-priority--high {
    background: rgba(255, 138, 138, 0.20);
    color: #ffb3b3;
}

.ms-supplier {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(100, 116, 139, 0.10);
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-supplier ::v-deep .v-icon {
    color: #64748b !important;
}

.ms-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-type-badge ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

.ms-temp-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--m-purple-soft);
    color: var(--m-purple);
    font-size: 0.78rem;
    font-weight: 800;
    direction: ltr;
}

.ms-temp-badge ::v-deep .v-icon {
    color: var(--m-purple) !important;
}

/* ── TEXT BLOCKS ── */
.ms-textblock {
    margin: 0;
    padding: 11px 13px;
    border-radius: 10px;
    background: #f7f9fc;
    border: 1px solid var(--m-border-soft);
    color: var(--m-text);
    font-size: 0.87rem;
    line-height: 1.65;
    white-space: pre-wrap;
}

.ms-textblock--empty {
    color: var(--m-text-mute);
    font-style: italic;
}

.ms-note {
    margin-top: 11px;
    padding: 11px 13px;
    border-radius: 10px;
    background: var(--m-orange-soft);
    border: 1px solid rgba(192, 105, 28, 0.18);
}

.ms-note__label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 5px;
    color: var(--m-orange);
    font-size: 0.75rem;
    font-weight: 800;
}

.ms-note__label ::v-deep .v-icon {
    color: var(--m-orange) !important;
}

.ms-note__text {
    color: var(--m-text);
    font-size: 0.86rem;
    line-height: 1.6;
    white-space: pre-wrap;
}

/* ── FILES ── */
.ms-files-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
}

.ms-file {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 11px;
    border-radius: 11px;
    background: #fff;
    border: 1px solid var(--m-border);
    transition: all 0.15s var(--m-ease);
}

.ms-file:hover {
    border-color: var(--m-indigo);
    background: var(--m-indigo-soft);
    transform: translateY(-1px);
}

.ms-file__main {
    display: flex;
    align-items: center;
    gap: 11px;
    flex: 1;
    min-width: 0;
    padding: 0;
    background: none;
    border: none;
    text-align: right;
    cursor: pointer;
    font-family: inherit;
}

.ms-file__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.ms-file__btn {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-file__btn:hover:not(:disabled) {
    background: var(--m-indigo);
    border-color: var(--m-indigo);
    color: #fff;
}

.ms-file__btn:hover:not(:disabled) ::v-deep .v-icon {
    color: #fff !important;
}

.ms-file__btn:disabled {
    cursor: default;
}

.ms-file__thumb {
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: 9px;
    overflow: hidden;
    background: var(--m-purple-soft);
    color: var(--m-purple);
}

.ms-file__thumb--pdf {
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-file__thumb--pdf ::v-deep .v-icon {
    color: var(--m-red) !important;
}

.ms-file__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ms-file__info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.ms-file__label {
    color: var(--m-text);
    font-size: 0.85rem;
    font-weight: 700;
}

.ms-file__meta {
    color: var(--m-text-mute);
    font-size: 0.73rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}



/* ── TIMELINE ── */
.ms-timeline {
    margin: 0;
    padding: 0 6px 0 0;
    list-style: none;
}

.ms-timeline__item {
    position: relative;
    padding: 0 22px 16px 0;
    border-right: 2px solid var(--m-border);
}

.ms-timeline__item--last {
    padding-bottom: 0;
    border-right-color: transparent;
}

.ms-timeline__dot {
    position: absolute;
    top: 2px;
    right: -7px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--m-border);
}

.ms-timeline__dot--open {
    background: var(--m-blue);
}

.ms-timeline__dot--progress {
    background: var(--m-orange);
}

.ms-timeline__dot--completed {
    background: var(--m-green);
}

.ms-timeline__dot--canceled {
    background: var(--m-red);
}

.ms-timeline__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: -3px;
}

.ms-timeline__status {
    color: var(--m-text);
    font-size: 0.87rem;
    font-weight: 800;
}

.ms-timeline__date {
    color: var(--m-text-soft);
    font-size: 0.76rem;
    direction: ltr;
    text-align: right;
}

.ms-timeline__by,
.ms-timeline__note {
    color: var(--m-text-mute);
    font-size: 0.76rem;
}

.ms-timeline__note {
    margin-top: 3px;
    padding: 5px 9px;
    border-radius: 7px;
    background: #f7f9fc;
    color: var(--m-text-soft);
}

/* ── FOOTER ── */
.ms-dlg-footer {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 9px;
    padding: 13px 18px;
    background: #fff;
    border-top: 1px solid var(--m-border-soft);
}

.ms-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 20px;
    border-radius: 9px;
    font-family: inherit;
    font-size: 0.86rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-btn--primary {
    background: var(--m-navy);
    border: 1px solid var(--m-navy);
    color: #fff;
}

.ms-btn--primary:hover {
    background: #142f78;
    box-shadow: 0 6px 14px -5px rgba(13, 44, 109, 0.55);
}

.ms-btn--primary ::v-deep .v-icon {
    color: #fff !important;
}

.ms-btn--ghost {
    background: transparent;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
}

.ms-btn--ghost:hover {
    background: #f1f5f9;
    color: var(--m-text);
}

@media (max-width: 720px) {
    .ms-dlg-grid {
        grid-template-columns: 1fr;
    }

    .ms-field {
        grid-template-columns: 1fr;
        gap: 2px;
    }

    .ms-dlg-header {
        flex-direction: column;
    }
}
</style>

<style>
.ms-dialog-wrap {
    border-radius: 16px !important;
    box-shadow: 0 24px 60px -18px rgba(13, 44, 109, 0.4) !important;
}
</style>